import React, { useContext, useEffect, useState } from 'react'
import {
  Table,
  Tbody,
  Tr,
  Td,
  Button,
  Input,
  Flex,
  Box,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { CachePolicies, useFetch } from 'use-http'
import { useTranslation } from 'react-i18next'
import useToast from '../../../hooks/useToast'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import type { MediaLibrarySearchFileResponse } from '../../../interfaces'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'

interface CC {
  'x-coordinate': number
  'y-coordinate': number
  width: number
  height: number
}
interface Props {
  changedCustomCoordinates: CC
  file: FileDetailsResponse | MediaLibrarySearchFileResponse
  setChangedCustomCoordinates(cc: CC): void
  onClose(): void
  getFileDetails(): Promise<FileDetailsResponse> | Promise<void>
}

const CustomCoordinatesTable = ({
  changedCustomCoordinates,
  file,
  getFileDetails,
  setChangedCustomCoordinates,
  onClose,
}: Props): ReactElement => {
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { t } = useTranslation()
  const [savingCustomCoordinate, setsavingCustomCoordinate] = useState(false)
  const [, dispatch] = useContext(MLContext)

  const saveCustomCoordinates = async (ccToUpdate: string): Promise<void> => {
    let ccAPIPayload: string | null = ccToUpdate
    if (ccToUpdate === '0,0,0,0') {
      // all cc are empty
      ccAPIPayload = null
    }

    const response = (await request.patch(
      `/api/v2/files/${file.fileId}/details`,
      {
        customCoordinates: ccAPIPayload,
      },
    )) as FileDetailsResponse

    setsavingCustomCoordinate(false)

    if (request.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    await getFileDetails()

    if (ccToUpdate) {
      toast({
        title: t(
          'custom_focus_area_updated_successfully',
          'Custom focus area updated successfully',
        ),
        status: 'success',
        position: 'top-right',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
      })
    } else {
      toast({
        title: t(
          'custom_focus_area_removed_successfully',
          'Custom focus area removed successfully',
        ),
        status: 'success',
        position: 'top-right',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
      })
    }
  }

  useEffect(() => {
    const customCoordinatesString = file.customCoordinates ?? ''
    const ccDbArray = file.customCoordinates
      ? customCoordinatesString.split(',')
      : []

    const changedCC = {
      'x-coordinate': ccDbArray[0] ? Number(ccDbArray[0]) : 0,
      'y-coordinate': ccDbArray[1] ? Number(ccDbArray[1]) : 0,
      width: ccDbArray[2] ? Number(ccDbArray[2]) : 0,
      height: ccDbArray[3] ? Number(ccDbArray[3]) : 0,
    }
    setChangedCustomCoordinates(changedCC)
  }, [file.customCoordinates])

  function isCCValueValid(name: string, value: number): boolean {
    if (value < 0) {
      toast({
        description: t('value_cannot_be_negative', 'Value cannot be negative'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
    if (name === 'height' && value === 0) {
      toast({
        description: t('height_cannot_be_zero', 'Height cannot be zero'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
    if (name === 'width' && value === 0) {
      toast({
        description: t('width_cannot_be_zero', 'Width cannot be zero'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }

    const currentYcoord = Number(changedCustomCoordinates['y-coordinate'])
    const currentXcoord = Number(changedCustomCoordinates['x-coordinate'])
    const currentHeight = Number(changedCustomCoordinates.height)
    const currentWidth = Number(changedCustomCoordinates.width)

    if (
      name === 'x-coordinate' &&
      file.width &&
      file.width <= Number(value) + currentWidth
    ) {
      toast({
        description: `${t(
          'box_out_of_bound_image_width',
          'Box out of bound. Image width',
        )} ${file.width}px`,
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
    if (
      name === 'y-coordinate' &&
      file.height &&
      file.height <= Number(value) + currentHeight
    ) {
      toast({
        description: `${t(
          'box_out_of_bound_image_height',
          'Box out of bound. Image height',
        )} ${file.height}px`,
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
    if (
      name === 'width' &&
      file.width &&
      file.width < Number(value) + currentXcoord
    ) {
      toast({
        description: `${t(
          'box_out_of_bound_image_width',
          'Box out of bound. Image width',
        )} ${file.width}px`,
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }
    if (
      name === 'height' &&
      file.height &&
      file.height < Number(value) + currentYcoord
    ) {
      toast({
        description: `${t(
          'box_out_of_bound_image_height',
          'Box out of bound. Image height',
        )} ${file.height}px`,
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    }

    return true
  }

  function handlerCustomCoordinateChange(name: string, value: string): void {
    if (!isCCValueValid(name, Number(value))) {
      return
    }

    const newCC = { ...changedCustomCoordinates }
    newCC[name] = parseInt(value, 10)
    setChangedCustomCoordinates(newCC)
  }

  return (
    <React.Fragment>
      <Table basic="very" className="custom-coordinates-table">
        <Tbody>
          <Tr>
            <Td>X coordinate</Td>
            <Td>
              <Input
                name="x-coordinate"
                type="number"
                onChange={e =>
                  void handlerCustomCoordinateChange(
                    e.target.name,
                    e.target.value,
                  )
                }
                value={changedCustomCoordinates['x-coordinate']}
                placeholder=""
              />
            </Td>
          </Tr>
          <Tr>
            <Td>Y coordinate</Td>
            <Td>
              <Input
                name="y-coordinate"
                type="number"
                onChange={e =>
                  void handlerCustomCoordinateChange(
                    e.target.name,
                    e.target.value,
                  )
                }
                value={changedCustomCoordinates['y-coordinate']}
                placeholder=""
              />
            </Td>
          </Tr>
          <Tr>
            <Td>Width</Td>
            <Td>
              <Input
                name="width"
                type="number"
                onChange={e =>
                  void handlerCustomCoordinateChange(
                    e.target.name,
                    e.target.value,
                  )
                }
                value={changedCustomCoordinates.width}
                placeholder=""
              />
            </Td>
          </Tr>
          <Tr>
            <Td>Height</Td>
            <Td>
              <Input
                name="height"
                type="number"
                onChange={e =>
                  void handlerCustomCoordinateChange(
                    e.target.name,
                    e.target.value,
                  )
                }
                value={changedCustomCoordinates.height}
                placeholder=""
              />
            </Td>
          </Tr>
        </Tbody>
      </Table>
      <Flex justifyContent="spcae-between" marginTop="15px">
        <Box flex={1} marginRight="5px">
          <Button
            isFullWidth
            variant="solid"
            isLoading={savingCustomCoordinate}
            onClick={() => {
              if (savingCustomCoordinate) {
                return false
              }
              setsavingCustomCoordinate(true)

              const ccToUpdate = `${changedCustomCoordinates['x-coordinate']},${changedCustomCoordinates['y-coordinate']},${changedCustomCoordinates.width},${changedCustomCoordinates.height}`

              void saveCustomCoordinates(ccToUpdate)

              // App.trackEvent('CustomFocusSubmitted')
              return true
            }}
          >
            {t('save', 'Save')}
          </Button>
        </Box>
        <Box flex={1} marginLeft="5px">
          <Button
            isFullWidth
            variant="outline"
            onClick={() => {
              dispatch({ type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS })
              onClose()
            }}
          >
            {t('cancel', 'Cancel')}
          </Button>
        </Box>
      </Flex>
    </React.Fragment>
  )
}

export default CustomCoordinatesTable
